"use strict";
/**
 * Kit-Compatible Signer Factories
 *
 * Framework-agnostic factory functions to create signers from wallet functions.
 * These factories enable pure Kit integration without React or framework dependencies.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createMessageSignerFromWallet = createMessageSignerFromWallet;
exports.createTransactionSendingSignerFromWallet = createTransactionSendingSignerFromWallet;
var bs58_1 = require("bs58");
var utils_1 = require("./utils");
var errors_1 = require("@hermis/errors");
/**
 * Create a MessageModifyingSigner from a wallet's sign message function
 *
 * This is a pure, framework-agnostic factory function that can be used anywhere.
 *
 * @param walletAddress - The address of the wallet
 * @param signMessageFn - Function to sign a message (from wallet adapter)
 * @returns A frozen MessageModifyingSigner object
 *
 * @example
 * ```typescript
 * const signer = createMessageSignerFromWallet(
 *   address('...'),
 *   async (msg) => await wallet.signMessage(msg)
 * )
 * ```
 */
function createMessageSignerFromWallet(walletAddress, signMessageFn) {
    var signer = {
        address: walletAddress,
        modifyAndSignMessages: function (messages, config) {
            return __awaiter(this, void 0, Promise, function () {
                var message, content, originalSignatures, signature, signatures;
                var _a;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            // Most wallets only support signing one message at a time
                            if (messages.length !== 1) {
                                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__INVALID_ARGUMENT, {
                                    argumentName: 'messages',
                                    receivedValue: "array of ".concat(messages.length, " messages"),
                                    expectedType: 'single message (array of length 1)'
                                });
                            }
                            message = messages[0];
                            content = message.content, originalSignatures = message.signatures;
                            // Handle abort signal if provided
                            if ((_a = config === null || config === void 0 ? void 0 : config.abortSignal) === null || _a === void 0 ? void 0 : _a.aborted) {
                                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_TRANSACTION, {});
                            }
                            return [4 /*yield*/, signMessageFn(content)
                                // Update signatures (handle potential message modification)
                            ];
                        case 1:
                            signature = _b.sent();
                            signatures = (0, utils_1.updateSignatureDictionary)(content, content, // Message content doesn't change in signMessage
                            originalSignatures, walletAddress, signature);
                            // Return signed message
                            return [2 /*return*/, [{
                                        content: content,
                                        signatures: signatures
                                    }]];
                    }
                });
            });
        }
    };
    return (0, utils_1.freezeSigner)(signer);
}
/**
 * Create a TransactionSendingSigner from a wallet's send transaction function
 *
 * This is a pure, framework-agnostic factory function that can be used anywhere.
 *
 * @param walletAddress - The address of the wallet
 * @param chain - The Solana chain identifier (e.g., 'solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1')
 * @param sendTransactionFn - Function to send a transaction (from wallet adapter)
 * @returns A frozen TransactionSendingSigner object
 *
 * @example
 * ```typescript
 * const signer = createTransactionSendingSignerFromWallet(
 *   address('...'),
 *   'solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1',
 *   async (tx) => await wallet.sendTransaction(tx, connection)
 * )
 * ```
 */
function createTransactionSendingSignerFromWallet(walletAddress, chain, sendTransactionFn) {
    var signer = {
        address: walletAddress,
        signAndSendTransactions: function (transactions, config) {
            return __awaiter(this, void 0, Promise, function () {
                var transaction, signatureString, signatureBytes;
                var _a;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            // Most wallets only support signing one transaction at a time
                            if (transactions.length !== 1) {
                                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__INVALID_ARGUMENT, {
                                    argumentName: 'transactions',
                                    receivedValue: "array of ".concat(transactions.length, " transactions"),
                                    expectedType: 'single transaction (array of length 1)'
                                });
                            }
                            transaction = transactions[0];
                            // Handle abort signal if provided
                            if ((_a = config === null || config === void 0 ? void 0 : config.abortSignal) === null || _a === void 0 ? void 0 : _a.aborted) {
                                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_TRANSACTION, {});
                            }
                            return [4 /*yield*/, sendTransactionFn(transaction)
                                // Decode the base58 signature string into the 64-byte Ed25519 signature
                            ];
                        case 1:
                            signatureString = _b.sent();
                            signatureBytes = bs58_1.default.decode(signatureString);
                            return [2 /*return*/, [signatureBytes]];
                    }
                });
            });
        }
    };
    return (0, utils_1.freezeSigner)(signer);
}
